/**
 * Agency portal top bar (SYR-85).
 *
 * Sidebar toggle · agency name + AGENCY chip · global period pill · user menu.
 * Same sticky 65px bar language as the marketer-view top bar.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Button, Dropdown, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined,
    LogoutOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import { THEME } from '../../components/MarketerView/config/theme';
import { AgencyPeriodPill } from './AgencyPeriodPill';

const { Text } = Typography;

interface AgencyTopBarProps {
    collapsed: boolean;
    isMobile: boolean;
    onToggle: () => void;
}

export const AgencyTopBar: React.FunctionComponent<AgencyTopBarProps> = ({
    collapsed,
    isMobile,
    onToggle,
}) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const userMenu: MenuProps = {
        items: [
            {
                key: 'profile',
                icon: <UserOutlined />,
                label: intl.formatMessage({
                    id: 'agency.userMenu.profile',
                    defaultMessage: 'Profile',
                }),
            },
            { type: 'divider' },
            {
                key: 'logout',
                icon: <LogoutOutlined />,
                danger: true,
                label: intl.formatMessage({
                    id: 'agency.userMenu.signOut',
                    defaultMessage: 'Sign out',
                }),
            },
        ],
        onClick: ({ key }) => {
            if (key === 'logout') logout();
            else if (key === 'profile') navigate('/agency/profile');
        },
    };

    const agencyName =
        user?.organisation?.name ||
        intl.formatMessage({ id: 'agency.shell.tagline', defaultMessage: 'Agency Portal' });
    const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;

    return (
        <div
            style={{
                height: 65,
                background: '#fff',
                borderBottom: `1px solid ${THEME.border}`,
                boxShadow: '0 1px 3px 0 rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: isMobile ? '0 16px' : '0 24px',
                position: 'sticky',
                top: 0,
                zIndex: 100,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
                <Button
                    type="text"
                    icon={
                        isMobile || collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                    }
                    onClick={onToggle}
                    style={{ fontSize: 16, width: 40, height: 40, flexShrink: 0 }}
                />
                <Text
                    style={{
                        fontSize: isMobile ? 16 : 20,
                        fontWeight: 600,
                        color: '#1f2937',
                        maxWidth: isMobile ? '40vw' : 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {agencyName}
                </Text>
                {!isMobile && (
                    <Tag
                        style={{
                            background: THEME.primarySoft,
                            color: THEME.primaryDark,
                            border: 'none',
                            fontWeight: 700,
                            fontSize: 11,
                            letterSpacing: '0.06em',
                            borderRadius: 999,
                            padding: '2px 10px',
                        }}
                    >
                        {intl.formatMessage({
                            id: 'agency.shell.chip',
                            defaultMessage: 'AGENCY',
                        })}
                    </Tag>
                )}
                {!isMobile && <AgencyPeriodPill />}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {isMobile && <AgencyPeriodPill />}
                <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                            padding: '6px 8px',
                            borderRadius: 8,
                        }}
                    >
                        <Avatar
                            style={{ background: THEME.primary }}
                            icon={<UserOutlined />}
                        />
                        {!isMobile && (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Text style={{ fontWeight: 500, color: '#1f2937', lineHeight: 1.2 }}>
                                    {displayName}
                                </Text>
                                <Text style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.2 }}>
                                    {intl.formatMessage({
                                        id: 'agency.shell.roleLabel',
                                        defaultMessage: 'Agency',
                                    })}
                                </Text>
                            </div>
                        )}
                    </div>
                </Dropdown>
            </div>
        </div>
    );
};
